import { useIsCircleAiEnabled } from "@circle-react/components/CircleAi/useCircleAiFeatureFlags";
import { useIsCommunityAdmin } from "@circle-react/contexts/punditUserContext";
import { useIsAdminOrModerator } from "@circle-react/contexts/punditUserContext";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Icon as CompassIcon } from "@circleco/compass/components/Icon";
import { ScrollArea } from "@circleco/compass/components/ScrollArea";
import { Tooltip } from "@circleco/compass/components/Tooltip";
import { Link, useRouterState } from "@tanstack/react-router";

import { t } from "@/i18n-js/instance";

import { useCircleAiStore } from "../useCircleAiStore";
import { CircleAIDockBuildMenu } from "./CircleAIDockBuildMenu";
import {
  DOCK_NAV_ACTIVE_CLASSES,
  DOCK_NAV_ICON_BUTTON_CLASSES,
} from "./constants";
import { DockNavBadgeDot } from "./DockNavBadgeDot";
import { isPathActive } from "./isPathActive";
import { useBuildNavItems } from "./useBuildNavItems";
import { usePrimaryNavSectionWithBadge } from "./useDockNavSections";

const GROUP_CLASSES = "flex flex-col items-center gap-2";
const DIVIDER_CLASSES = "border-primary w-6 border-t";

const SCROLL_AREA_STYLE = {
  "--scroll-area-fade-color": "var(--color-background-secondary)",
} as React.CSSProperties;

export const CircleAIDockNavCollapsed = () => {
  const isAdmin = useIsCommunityAdmin();
  const isCircleAiEnabled = useIsCircleAiEnabled();
  const canUseBuildNav = useIsAdminOrModerator();
  const primarySection = usePrimaryNavSectionWithBadge();
  const buildItems = useBuildNavItems();
  const shouldShowRecentChats = isAdmin && isCircleAiEnabled;
  const pathname = useRouterState({ select: s => s.location.pathname });
  const setDockRecentChatsOpen = useCircleAiStore(
    s => s.setDockRecentChatsOpen,
  );

  return (
    <ScrollArea.Root
      scrollFades
      style={SCROLL_AREA_STYLE}
      className="relative h-full min-h-0"
    >
      <ScrollArea.Viewport className="size-full">
        <div className="flex flex-col items-center gap-2 p-4">
          {primarySection.items.length > 0 && (
            <div className={GROUP_CLASSES}>
              {primarySection.items.map(item => {
                const isActive = item.to
                  ? isPathActive({
                      exact: item.exact,
                      pathname,
                      to: item.to,
                    })
                  : Boolean(item.isActive);
                const iconName =
                  isActive && item.activeIcon ? item.activeIcon : item.icon;
                const itemIcon = (
                  <div className="relative">
                    <CompassIcon name={iconName} size="md" color="primary" />
                    {item.badge && <DockNavBadgeDot />}
                  </div>
                );

                return (
                  <Tooltip key={item.id} content={item.label} side="right">
                    {item.to ? (
                      <Link
                        to={item.to}
                        aria-label={item.label}
                        data-testid={item["data-testid"]}
                        data-circle-ai-toggle={
                          item.id === "circle-ai" ? "dock" : undefined
                        }
                        activeOptions={{
                          exact: item.exact ?? false,
                          includeSearch: false,
                        }}
                        className={classNames(
                          DOCK_NAV_ICON_BUTTON_CLASSES,
                          "relative",
                          isActive && DOCK_NAV_ACTIVE_CLASSES,
                        )}
                      >
                        {itemIcon}
                      </Link>
                    ) : (
                      <button
                        type="button"
                        onClick={item.onClick}
                        disabled={item.disabled}
                        aria-label={item.label}
                        data-testid={item["data-testid"]}
                        data-circle-ai-toggle={
                          item.id === "circle-ai" ? "dock" : undefined
                        }
                        className={classNames(
                          DOCK_NAV_ICON_BUTTON_CLASSES,
                          "relative",
                          item.disabled && "pointer-events-none opacity-50",
                          isActive && DOCK_NAV_ACTIVE_CLASSES,
                        )}
                        {...(item.isActive !== undefined && {
                          "aria-pressed": item.isActive,
                        })}
                      >
                        {itemIcon}
                      </button>
                    )}
                  </Tooltip>
                );
              })}
            </div>
          )}

          {shouldShowRecentChats && (
            <>
              <div className={DIVIDER_CLASSES} />
              <div className={GROUP_CLASSES}>
                <Tooltip
                  content={t("circle_ai_home.recent_chats_drawer.open")}
                  side="right"
                >
                  <button
                    type="button"
                    onClick={() => setDockRecentChatsOpen(true)}
                    aria-label={t("circle_ai_home.recent_chats_drawer.open")}
                    className={DOCK_NAV_ICON_BUTTON_CLASSES}
                  >
                    <CompassIcon name="clock-dash" size="md" color="primary" />
                  </button>
                </Tooltip>
              </div>
            </>
          )}

          {canUseBuildNav && buildItems.length > 0 && (
            <>
              <div className={DIVIDER_CLASSES} />

              <div className={GROUP_CLASSES}>
                <CircleAIDockBuildMenu items={buildItems} />
              </div>
            </>
          )}
        </div>
      </ScrollArea.Viewport>
      <ScrollArea.Scrollbar orientation="vertical" />
    </ScrollArea.Root>
  );
};
